<template>
  <component
    v-show="!isFetching"
    v-for="notification in topNotifications"
    :is="notification?.component"
    :key="notification?.id"
    :severity="notification?.impact"
    :id="notification?.id"
    :extraParameters="notification?.extraParams"
    :dismissible="notification?.isDismissible"
    :ttl="notification?.ttl"
  />
</template>

<script setup>
import { computed, ref, provide, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import * as Sentry from '@sentry/vue'

import NotificationBudgetAlmostRunout from '@/components/productNotifications/NotificationBudgetAlmostRunout.vue'
import NotificationBudgetRunout from '@/components/productNotifications/NotificationBudgetRunout.vue'
import NotificationCampaignInactive from '@/components/productNotifications/NotificationCampaignInactive.vue'
import NotificationCampaignPausedCPC from '@/components/productNotifications/NotificationCampaignPausedCPC.vue'
import NotificationCampaignPausedUntilNetCPA from '@/components/productNotifications/NotificationCampaignPausedUntilNetCPA.vue'
import NotificationNoI2PConnection from '@/components/productNotifications/NotificationNoI2PConnection.vue'
import NotificationPartnerInactive from '@/components/productNotifications/NotificationPartnerInactive.vue'
import NotificationPartnerAndConnectionInactive from '@/components/productNotifications/NotificationPartnerAndConnectionInactive.vue'
import NotificationPartnerOrConnectionInactiveAlternativePartner from '@/components/productNotifications/NotificationPartnerOrConnectionInactiveAlternativePartner.vue'
import NotificationPartnerOrConnectionInactiveMultiplePartners from '@/components/productNotifications/NotificationPartnerOrConnectionInactiveMultiplePartners.vue'
import NotificationPartnerObsolete from '@/components/productNotifications/NotificationPartnerObsolete.vue'
import NotificationPartnerObsoleteAlternative from '@/components/productNotifications/NotificationPartnerObsoleteAlternative.vue'
import NotificationUnavailability from '@/components/productNotifications/NotificationUnavailability.vue'
import NotificationLiveRateUnavailability from '@/components/productNotifications/NotificationLiveRateUnavailability.vue'
import NotificationActiveNetCpaEligible from '@/components/productNotifications/NotificationActiveNetCpaEligible.vue'
import NotificationConnectionActiveAlternativeProvider from '@/components/productNotifications/NotificationConnectionActiveAlternativeProvider.vue'
import NotificationPlusCancelled from '@/components/productNotifications/NotificationPlusCancelled.vue'
import NotificationHotelOfferSetup from '@/components/productNotifications/NotificationHotelOfferSetup.vue'
import NotificationContactData from '@/components/productNotifications/NotificationContactData.vue'
import NotificationCompetitorsSetup from '@/components/productNotifications/NotificationCompetitorsSetup.vue'

import { useNotifications } from '@/components/rateConnect/queries'

const route = useRoute()
const routeName = computed(() => (typeof route.name === 'string' ? route.name : String(route.name ?? '')))
const productType = computed(() => {
  if (routeName.value.includes('home')) {
    return 'BILLING'
  } else if (routeName.value.includes('rate-connect-running')) {
    return 'RATE_CONNECT'
  } else if (routeName.value.includes('property-details')) {
    return 'PROPERTY_DETAILS'
  } else if (routeName.value.includes('rate-insights')) {
    return 'PLUS,RATE_INSIGHTS'
  } else if (
    routeName.value.includes('rate-insights') ||
    routeName.value.includes('visitors-profile') ||
    routeName.value.includes('hotel-offers')
  ) {
    return 'PLUS'
  }
  return null
})

const { notificationsData, isFetching } = useNotifications(productType)

const topNotifications = computed(() => {
  if (!Array.isArray(notificationsData.value)) return []
  return notificationsData.value
    .filter(
      notification => notification.type && notificationToComponent[notification.type] && !isDismissed(notification.id)
    )
    .slice(0, 2)
    .map(notification => ({
      ...notification,
      component: notificationToComponent[notification.type],
    }))
})

const notificationToComponent = {
  ITEM_TO_PARTNER_CONNECTION_INACTIVE: NotificationNoI2PConnection,
  PARTNER_INACTIVE: NotificationPartnerInactive,
  PARTNER_INACTIVE_AND_ITEM_TO_PARTNER_CONNECTION_INACTIVE: NotificationPartnerAndConnectionInactive,
  PARTNER_INACTIVE_OR_ITEM_TO_PARTNER_CONNECTION_INACTIVE_ALTERNATIVE_PARTNER:
    NotificationPartnerOrConnectionInactiveAlternativePartner,
  PARTNER_INACTIVE_OR_ITEM_TO_PARTNER_CONNECTION_INACTIVE_MULTIPLE_ALTERNATIVE_PARTNERS:
    NotificationPartnerOrConnectionInactiveMultiplePartners,
  PARTNER_OBSOLETE: NotificationPartnerObsolete,
  PARTNER_OBSOLETE_ALTERNATIVE_PARTNER: NotificationPartnerObsoleteAlternative,
  CPC_CAMPAIGN_PAUSED: NotificationCampaignPausedCPC,
  NETCPA_CAMPAIGN_PAUSED: NotificationCampaignInactive,
  NETCPA_CAMPAIGN_PAUSED_UNTIL: NotificationCampaignPausedUntilNetCPA,
  BUDGET_RAN_OUT: NotificationBudgetRunout,
  RATE_UNAVAILABILITY_100_PERCENT: NotificationUnavailability,
  LIVE_RATE_UNAVAILABILITY_100_PERCENT: NotificationLiveRateUnavailability,
  BUDGET_ABOUT_TO_RUN_OUT: NotificationBudgetAlmostRunout,
  CPC_ACTIVE_NETCPA_ELIGIBLE: NotificationActiveNetCpaEligible,
  CONNECTION_ACTIVE_ALTERNATIVE_PROVIDER: NotificationConnectionActiveAlternativeProvider,
  CONNECTION_ACTIVE_MULTIPLE_ALTERNATIVE_PROVIDERS: NotificationConnectionActiveAlternativeProvider,
  PLUS_SUBSCRIPTION_CANCELLED: NotificationPlusCancelled,
  HOTEL_OFFERS_NOT_SET_UP: NotificationHotelOfferSetup,
  CONTACT_DETAILS_MISSING: NotificationContactData,
  COMPETITORS_NOT_SET_UP: NotificationCompetitorsSetup,
}

const dismissedNotifications = ref({})
const dismissedNotificationsStorageKey = 'dismissedNotifications'

const dismissNotification = (id, ttl = 0) => {
  const daysInMs = ttl * 24 * 60 * 60 * 1000
  const expirationDate = Date.now() + daysInMs

  dismissedNotifications.value[id] = expirationDate
  try {
    localStorage.setItem(dismissedNotificationsStorageKey, JSON.stringify(dismissedNotifications.value))
  } catch (e) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'notifications')
      Sentry.captureException(e)
    })
  }
}
provide('dismissNotification', dismissNotification)

const isDismissed = id => {
  const expirationDate = dismissedNotifications.value[id]
  if (!expirationDate) return false

  const now = Date.now()
  if (now >= expirationDate) {
    delete dismissedNotifications.value[id]
    localStorage.setItem(dismissedNotificationsStorageKey, JSON.stringify(dismissedNotifications.value))
    return false
  }

  return true
}

const loadDismissedNotifications = () => {
  try {
    const stored = localStorage.getItem(dismissedNotificationsStorageKey)
    if (!stored) return

    const parsed = JSON.parse(stored)
    const now = Date.now()
    let isAnyStoredDismissalExpired = false

    Object.entries(parsed).forEach(([id, expirationDate]) => {
      if (now < expirationDate) {
        dismissedNotifications.value[id] = expirationDate
      } else {
        isAnyStoredDismissalExpired = true
      }
    })

    if (isAnyStoredDismissalExpired) {
      localStorage.setItem(dismissedNotificationsStorageKey, JSON.stringify(dismissedNotifications.value))
    }
  } catch (e) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'notifications')
      Sentry.captureException(e)
    })
  }
}

onMounted(() => {
  loadDismissedNotifications()
})
</script>
